import { PaymentRequestModal } from '@lightspeed/invoicing-library'
import React from 'react'
import ReactDOM from 'react-dom'
import { ToastNotifications } from '@lightspeed/design-system-react'
import { PaymentRequestFormData } from '../../../../../scripts/models/invoicing-payment-request'
import { createPaymentRequestFromSaleScreen } from '../../../../functions/invoicing/create-payment-request-from-sale-screen'
import {
  buildPaymentRequestDto,
  getPreviewURL,
} from '../../../../functions/invoicing/payment-request-resources'
import {
  useRegisterManager,
  useRetailerSettingsManager,
  useSaleManager,
  useUserManager,
} from '../../../../ng-compat/hooks'
import { useAppDispatch, useAppSelector } from '../../../../state/hooks'
import {
  addPaymentRequestSaleAttribute,
  selectCustomer,
  selectLineItems,
  selectSaleAttributesHasPaymentRequest,
  selectSaleFinals,
  selectSaleId,
  selectSaleSummaryFields,
} from '../../../../state/slices/currentSale/currentSaleSlice'
import { useGoConfirmation } from '../hooks/useGoConfirmation'
import { setPaymentRequestSuccess } from '../../../../state/slices/posState/posStateSlice'
import { useFetchPaymentTypes } from '../hooks/useFetchPaymentTypes'
interface PaymentRequestModalProps {
  showPaymentRequestModal: boolean
  setShowPaymentRequestModal: () => void
  amountTendered: string // is the amount entered for partial payments on the Payment Panel, if not edited then equals the sale outstanding balance
}

export const PaymentRequestModalWrapper: React.FC<PaymentRequestModalProps> = ({
  showPaymentRequestModal,
  setShowPaymentRequestModal,
  amountTendered,
}) => {
  const registerManager = useRegisterManager()
  const currentOutlet = registerManager.getActiveOutlet()
  const currentRegister = registerManager.getActiveRegister()
  const customer = useAppSelector(selectCustomer)
  const lineItems = useAppSelector(selectLineItems)
  const saleIdFromStore = useAppSelector(selectSaleId)
  const saleFinals = useAppSelector(selectSaleFinals)
  const retailer = useRetailerSettingsManager().get()
  const { discount } = useAppSelector(selectSaleSummaryFields)
  const saleManager = useSaleManager()
  const dispatch = useAppDispatch()
  const goConfirmation = useGoConfirmation()
  const userManager = useUserManager()
  const user = userManager.getLoggedInUser()
  const hasPaymentRequestAttribute = useAppSelector(
    selectSaleAttributesHasPaymentRequest
  )
  const { systemPaymentTypes } = useFetchPaymentTypes()

  const paymentRequestDto = buildPaymentRequestDto({
    customer,
    lineItems,
    saleIdFromStore,
    saleFinals,
    currentOutlet,
    currentRegister,
    retailer,
    discount,
    amountTendered,
    user,
    lspayPaymentTypeId: systemPaymentTypes.lspayPaymentType?.id,
  })

  const previewURL = getPreviewURL(retailer)

  const addPaymentRequestAttribute = () => {
    if (!hasPaymentRequestAttribute) {
      dispatch(addPaymentRequestSaleAttribute())
    }
  }

  const setPaymentRequestSuccessState = (
    paymentRequestId: string,
    requestedAmount: number
  ) => {
    dispatch(
      setPaymentRequestSuccess({
        paymentRequestId,
        requestedAmount,
      })
    )
  }

  const showSuccessToast = (paymentRequestId: string, email: string) => {
    ToastNotifications.success(
      `Payment request #${paymentRequestId} sent to ${email}`
    )
  }

  const handleSubmit = async (formData: PaymentRequestFormData) => {
    const result = await createPaymentRequestFromSaleScreen(
      saleManager,
      formData.paymentRequestDto
    ) // Save sale and create payment request

    if (!result.success) {
      throw new Error(result.error || 'Failed to create payment request')
    }

    if (!result.paymentRequestId) {
      throw new Error('Payment request created but no ID returned')
    }

    setPaymentRequestSuccessState(
      result.paymentRequestId,
      result.requestedAmount || formData.paymentRequestDto.requestedAmount
    ) // Update payment request success state
    addPaymentRequestAttribute() // add payment request to sale attributes
    setShowPaymentRequestModal() // Close modal
    showSuccessToast(
      result.paymentRequestId,
      formData.paymentRequestDto.sendToEmail
    ) // Show success Toast
    goConfirmation() // Save sale and navigate to the sale confirmation screen.
  }

  if (!showPaymentRequestModal) return null

  return ReactDOM.createPortal(
    <PaymentRequestModal
      open={showPaymentRequestModal}
      setOpen={setShowPaymentRequestModal}
      customerEmail={''}
      setCustomerEmail={(_value: string) => {}} // No-op function as modal handles its own state
      documentURL={previewURL}
      onSubmit={handleSubmit}
      handleCancel={setShowPaymentRequestModal}
      paymentRequestDto={paymentRequestDto}
      showAmountField={false}
      showItemsToggle={false}
    />,
    document.body
  )
}
